/**
 * HTTP Error Classes
 * Specific error types for better error handling and user feedback
 * @module @infra/http
 */

import { getI18nService } from '@infra/i18n';
import type { TranslationKey } from '@infra/i18n';

/**
 * Copy for when the server sent no message of its own. Resolved per throw so it follows the
 * current language; the English argument covers a throw before i18n finished booting.
 */
function fallbackMessage(key: TranslationKey, english: string): string {
  return getI18nService().t(key, { defaultValue: english });
}

/**
 * Base HTTP Error class
 */
export class HttpError extends Error {
  status?: number;
  statusText?: string;
  code?: string;
  data?: unknown;
  /** Original Axios request config, preserved for retry scenarios */
  _requestConfig?: unknown;

  constructor(
    message: string,
    status?: number,
    statusText?: string,
    code?: string,
    data?: unknown
  ) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.statusText = statusText;
    this.code = code;
    this.data = data;
    Object.setPrototypeOf(this, HttpError.prototype);
  }
}

/**
 * Network error - when request fails due to network issues
 */
export class NetworkError extends HttpError {
  constructor(
    message: string = fallbackMessage(
      'errors:network.connectionFailed',
      'Could not reach the server. Please check your network connection.'
    ),
    _originalError?: unknown
  ) {
    super(message);
    this.name = 'NetworkError';
    this.code = 'NETWORK_ERROR';
    Object.setPrototypeOf(this, NetworkError.prototype);
  }
}

/**
 * Authentication error - 401 Unauthorized
 */
export class AuthError extends HttpError {
  constructor(
    message: string = fallbackMessage('errors:server.unauthorized', 'Unauthorized'),
    status: number = 401
  ) {
    super(message, status);
    this.name = 'AuthError';
    this.code = 'AUTH_ERROR';
    Object.setPrototypeOf(this, AuthError.prototype);
  }
}

/**
 * Forbidden error - 403 Forbidden
 */
export class ForbiddenError extends HttpError {
  constructor(
    message: string = fallbackMessage(
      'errors:server.noPermission',
      "You don't have permission to perform this action."
    ),
    status: number = 403
  ) {
    super(message, status);
    this.name = 'ForbiddenError';
    this.code = 'FORBIDDEN_ERROR';
    Object.setPrototypeOf(this, ForbiddenError.prototype);
  }
}

/**
 * Not Found error - 404 Not Found
 */
export class NotFoundError extends HttpError {
  resource?: string;

  constructor(
    message: string = fallbackMessage('errors:network.notFound', 'Resource not found'),
    resource?: string
  ) {
    super(message, 404);
    this.name = 'NotFoundError';
    this.code = 'NOT_FOUND_ERROR';
    this.resource = resource;
    Object.setPrototypeOf(this, NotFoundError.prototype);
  }
}

/**
 * Validation error - 422 Unprocessable Entity
 */
export class ValidationError extends HttpError {
  fields?: Record<string, string[]>;

  constructor(
    message: string = fallbackMessage('errors:validation.invalidData', 'Invalid data'),
    fields?: Record<string, string[]>
  ) {
    super(message, 422);
    this.name = 'ValidationError';
    this.code = 'VALIDATION_ERROR';
    this.fields = fields;
    Object.setPrototypeOf(this, ValidationError.prototype);
  }
}

/**
 * Server error - 5xx Server Errors
 */
export class ServerError extends HttpError {
  constructor(
    message: string = fallbackMessage(
      'errors:server.retryLater',
      'Server error. Please try again later.'
    ),
    status: number = 500
  ) {
    super(message, status);
    this.name = 'ServerError';
    this.code = 'SERVER_ERROR';
    Object.setPrototypeOf(this, ServerError.prototype);
  }
}

/**
 * The caller aborted the request — a component unmounted, a query was
 * superseded, StrictMode replayed a mount. Nothing failed and nothing reached
 * the server, so this is kept apart from {@link NetworkError}: reporting it as
 * "check your connection" sends readers hunting for an outage that never
 * happened.
 */
export class CanceledRequestError extends HttpError {
  constructor(message: string = 'Request was canceled.') {
    super(message);
    this.name = 'CanceledRequestError';
    this.code = 'CANCELED';
    Object.setPrototypeOf(this, CanceledRequestError.prototype);
  }
}

/**
 * Timeout error - Request timeout
 */
export class TimeoutError extends HttpError {
  constructor(
    message: string = fallbackMessage(
      'errors:network.requestTimeout',
      'Request timed out. Please try again.'
    )
  ) {
    super(message, 408);
    this.name = 'TimeoutError';
    this.code = 'TIMEOUT_ERROR';
    Object.setPrototypeOf(this, TimeoutError.prototype);
  }
}
